import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Rss, Plus, Trash2, ArrowUp, ArrowDown, ShieldCheck, Globe, Undo2,
} from 'lucide-react';
import type {
  BlogPostListRow, BlogPostDetail, BlogPostAdminStatus, EmailBlock,
} from '@evi/shared';
import {
  listBlogPostsAdmin, getBlogPostAdmin, createBlogPostAdmin, updateBlogPostAdmin,
  approveBlogPostAdmin, publishBlogPostAdmin, unpublishBlogPostAdmin,
  previewBlogHtmlAdmin, API_BASE_URL,
} from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './BlogPage.module.css';

/**
 * Blog studio — compose evinitiative.com/blog posts from STRUCTURED BLOCKS
 * (the same vocabulary as the newsletter), preview them through the SAME
 * server-side web renderer the public page uses, then APPROVE and PUBLISH.
 * Approve + Publish are the human gates: agents (MCP blog_draft_* tools) can
 * only draft — no agent path can approve or publish, ever.
 */

const BLOCK_TYPES: { type: EmailBlock['type']; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'paragraph', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'image_row', label: 'Image row (2–3)' },
  { type: 'button', label: 'Button' },
  { type: 'video_poster', label: 'Video (linked poster)' },
  { type: 'list', label: 'List (bullet / numbered)' },
  { type: 'quote', label: 'Quote' },
  { type: 'offer', label: 'Offer' },
  { type: 'divider', label: 'Divider' },
];

function defaultBlock(type: EmailBlock['type']): EmailBlock {
  switch (type) {
    case 'heading': return { type: 'heading', text: 'New section', level: 2 };
    case 'paragraph': return { type: 'paragraph', text: 'New paragraph. Use **bold**, *italic*, and http(s) links.' };
    case 'image': return { type: 'image', url: '', alt: '' };
    case 'image_row': return { type: 'image_row', images: [{ url: '', alt: '' }, { url: '', alt: '' }] };
    case 'button': return { type: 'button', label: 'Learn more', href: '' };
    case 'video_poster': return { type: 'video_poster', posterUrl: '', href: '', alt: '' };
    case 'list': return { type: 'list', style: 'bullet', items: ['First item'] };
    case 'quote': return { type: 'quote', text: 'Quoted text.' };
    case 'offer': return { type: 'offer', heading: 'Highlight', body: 'Details go here.' };
    case 'divider': return { type: 'divider' };
  }
}

function starterBlocks(): EmailBlock[] {
  return [
    { type: 'paragraph', text: 'Write your opening here.' },
  ];
}

const STATUS_LABEL: Record<BlogPostAdminStatus, string> = {
  draft: 'Draft', approved: 'Approved', published: 'Published',
};

/** Suggest a url-safe slug from a title (editable afterwards). */
function slugify(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 120);
}

interface EditorState {
  id: string | null;
  status: BlogPostAdminStatus;
  title: string;
  slug: string;
  excerpt: string;
  hero_image: string;
  category: string;
  author: string;
  blocks: EmailBlock[];
  preview_token: string | null;
}

function newEditor(): EditorState {
  return {
    id: null, status: 'draft', title: '', slug: '', excerpt: '',
    hero_image: '', category: '', author: '', blocks: starterBlocks(),
    preview_token: null,
  };
}

function editorFromDetail(d: BlogPostDetail): EditorState {
  return {
    id: d.id, status: d.status, title: d.title, slug: d.slug,
    excerpt: d.excerpt ?? '', hero_image: d.hero_image ?? '',
    category: d.category ?? '', author: d.author ?? '',
    blocks: d.blocks, preview_token: d.preview_token,
  };
}

export function BlogPage() {
  useRecentRoute('Blog');
  const qc = useQueryClient();

  const { data: list = [], isLoading, error } = useQuery({
    queryKey: ['blog', 'admin'],
    queryFn: listBlogPostsAdmin,
    staleTime: 15_000,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewErr, setPreviewErr] = useState('');

  const { data: detail } = useQuery({
    queryKey: ['blog', 'admin', selectedId],
    queryFn: () => getBlogPostAdmin(selectedId as string),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (detail && !dirty) setEditor(editorFromDetail(detail));
  }, [detail, dirty]);

  // Live web preview — debounce the in-progress blocks through the SAME
  // server renderer (P2) the public page uses, so preview === shipped HTML.
  const blocks = editor?.blocks ?? null;
  useEffect(() => {
    if (!blocks || blocks.length === 0) { setPreviewHtml(''); return undefined; }
    // `cancelled` is captured per effect-run: when blocks change, this run's
    // cleanup flips it so a slow in-flight preview can't overwrite a newer one
    // (out-of-order responses would otherwise show stale HTML).
    let cancelled = false;
    const t = setTimeout(() => {
      previewBlogHtmlAdmin(blocks)
        .then((html) => { if (!cancelled) { setPreviewHtml(html); setPreviewErr(''); } })
        .catch((e: unknown) => { if (!cancelled) setPreviewErr(e instanceof Error ? e.message : 'Preview failed'); });
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [blocks]);

  const readOnly = !!editor && editor.status !== 'draft';

  function edit(patch: Partial<EditorState>) {
    setEditor((e) => (e ? { ...e, ...patch } : e));
    setDirty(true);
    setBanner(null);
  }
  function setBlocks(next: EmailBlock[]) { edit({ blocks: next }); }

  // Switching away from an unsaved editor discards its edits (the hydration
  // effect overwrites `editor` once the new selection's detail loads), so guard
  // it — same discipline as the approve/publish confirms below.
  function confirmDiscardIfDirty(): boolean {
    if (dirty && editor) return window.confirm('Discard unsaved changes to this post?');
    return true;
  }
  function openNew() {
    if (!confirmDiscardIfDirty()) return;
    setSelectedId(null);
    setEditor(newEditor());
    setDirty(true);
    setBanner(null);
  }
  function openExisting(id: string) {
    if (id === selectedId) return;
    if (!confirmDiscardIfDirty()) return;
    setSelectedId(id);
    setDirty(false);
    setBanner(null);
  }

  const invalidate = (id?: string | null) => {
    qc.invalidateQueries({ queryKey: ['blog', 'admin'] });
    if (id) qc.invalidateQueries({ queryKey: ['blog', 'admin', id] });
  };

  const save = useMutation({
    mutationFn: async (e: EditorState) => {
      const body = {
        title: e.title, slug: e.slug, blocks: e.blocks,
        excerpt: e.excerpt || null, hero_image: e.hero_image || null,
        author: e.author || null, category: e.category || null,
      };
      return e.id ? updateBlogPostAdmin(e.id, body) : createBlogPostAdmin(body);
    },
    onSuccess: (d) => {
      // Hydrate the editor from the server's canonical detail immediately so
      // editor.id is set right away — otherwise a create leaves id=null until
      // the background refetch lands, and a second click would re-POST the
      // same slug and 409 on the unique index.
      setSelectedId(d.id);
      setEditor(editorFromDetail(d));
      setDirty(false);
      setBanner({ kind: 'ok', text: 'Draft saved.' });
      invalidate(d.id);
    },
    onError: (e: unknown) => setBanner({ kind: 'err', text: e instanceof Error ? e.message : 'Save failed' }),
  });

  const approve = useMutation({
    mutationFn: (id: string) => approveBlogPostAdmin(id),
    onSuccess: (d) => { setBanner({ kind: 'ok', text: 'Approved — preview link below; publish when ready.' }); invalidate(d.id); },
    onError: (e: unknown) => setBanner({ kind: 'err', text: e instanceof Error ? e.message : 'Approve failed' }),
  });
  const publish = useMutation({
    mutationFn: (id: string) => publishBlogPostAdmin(id),
    onSuccess: (d) => { setBanner({ kind: 'ok', text: 'Published — live on the public blog API.' }); invalidate(d.id); },
    onError: (e: unknown) => setBanner({ kind: 'err', text: e instanceof Error ? e.message : 'Publish failed' }),
  });
  const unpublish = useMutation({
    mutationFn: (id: string) => unpublishBlogPostAdmin(id),
    onSuccess: (d) => { setBanner({ kind: 'ok', text: 'Unpublished — back to an editable draft.' }); invalidate(d.id); },
    onError: (e: unknown) => setBanner({ kind: 'err', text: e instanceof Error ? e.message : 'Unpublish failed' }),
  });

  const busy = save.isPending || approve.isPending || publish.isPending || unpublish.isPending;
  const canSave = !!editor && !readOnly
    && editor.title.trim().length > 0 && editor.slug.trim().length > 0 && editor.blocks.length > 0;

  const previewUrl = editor?.status === 'approved' && editor.preview_token
    ? `${API_BASE_URL}/public/blog/${editor.slug}?preview=${editor.preview_token}`
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Rss size={20} /></div>
          <div>
            <h1 className={styles.title}>Blog</h1>
            <p className={styles.subtitle}>
              Compose evinitiative.com/blog posts from blocks. Agents draft; you approve and publish.
            </p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={openNew} type="button">
          <Plus size={15} /> New post
        </button>
      </div>

      <div className={styles.body}>
        {/* ── List ───────────────────────────────────────────────── */}
        <aside className={styles.listCol}>
          {isLoading && <p className={styles.muted}>Loading…</p>}
          {error && <p className={styles.err}>{(error as Error).message}</p>}
          {!isLoading && list.length === 0 && <p className={styles.muted}>No posts yet.</p>}
          <ul className={styles.list}>
            {list.map((row: BlogPostListRow) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`${styles.listItem} ${selectedId === row.id ? styles.listItemActive : ''}`}
                  onClick={() => openExisting(row.id)}
                >
                  <span className={styles.listTitle}>{row.title || '(untitled)'}</span>
                  <span className={styles.listMeta}>
                    <span className={`${styles.badge} ${styles[`badge_${row.status}`]}`}>{STATUS_LABEL[row.status]}</span>
                    <span className={styles.listSlug}>/{row.slug}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Editor + live web preview ──────────────────────────── */}
        <section className={styles.editorCol}>
          {!editor && <div className={styles.empty}>Select a post, or start a new one.</div>}

          {editor && (
            <>
              <div className={styles.editorHead}>
                <span className={`${styles.badge} ${styles[`badge_${editor.status}`]}`}>{STATUS_LABEL[editor.status]}</span>
                {dirty && <span className={styles.dirtyTag}>unsaved changes</span>}
              </div>

              {banner && (
                <div className={banner.kind === 'ok' ? styles.okBanner : styles.errBanner}>{banner.text}</div>
              )}

              {readOnly && (
                <div className={styles.frozenBanner}>
                  This post is <strong>{STATUS_LABEL[editor.status].toLowerCase()}</strong> and can no longer be edited.
                  {editor.status === 'published' && ' Unpublish to pull it back to an editable draft.'}
                </div>
              )}

              {previewUrl && (
                <div className={styles.previewLink}>
                  Site preview (approved, unpublished): <code>{previewUrl}</code>
                </div>
              )}

              <div className={styles.split}>
                {/* Form side */}
                <div className={styles.formSide}>
                  <label className={styles.field}>
                    <span className={styles.label}>Title</span>
                    <input
                      className={styles.input}
                      value={editor.title}
                      disabled={readOnly}
                      maxLength={200}
                      placeholder="Post title"
                      onChange={(e) => {
                        const title = e.target.value;
                        // Auto-suggest the slug while the post is NEW and the
                        // slug hasn't been hand-edited away from the suggestion.
                        const followSlug = !editor.id && (editor.slug === '' || editor.slug === slugify(editor.title));
                        edit(followSlug ? { title, slug: slugify(title) } : { title });
                      }}
                    />
                  </label>
                  <div className={styles.metaGrid}>
                    <label className={styles.field}>
                      <span className={styles.label}>Slug</span>
                      <input className={styles.input} value={editor.slug} disabled={readOnly} maxLength={120}
                        placeholder="url-safe-slug" onChange={(e) => edit({ slug: e.target.value })} />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>Category</span>
                      <input className={styles.input} value={editor.category} disabled={readOnly} maxLength={80}
                        placeholder="Updates" onChange={(e) => edit({ category: e.target.value })} />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>Author</span>
                      <input className={styles.input} value={editor.author} disabled={readOnly} maxLength={120}
                        placeholder="EV Initiative" onChange={(e) => edit({ author: e.target.value })} />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>Hero image URL (https)</span>
                      <input className={styles.input} value={editor.hero_image} disabled={readOnly} maxLength={2000}
                        placeholder="https://…" onChange={(e) => edit({ hero_image: e.target.value })} />
                    </label>
                  </div>
                  <label className={styles.field}>
                    <span className={styles.label}>Excerpt (card teaser)</span>
                    <textarea className={styles.textarea} value={editor.excerpt} disabled={readOnly} maxLength={500}
                      onChange={(e) => edit({ excerpt: e.target.value })} />
                  </label>

                  <div className={styles.blocksHead}>
                    <span className={styles.label}>Content blocks</span>
                    {!readOnly && (
                      <select
                        className={styles.addSelect}
                        value=""
                        onChange={(e) => {
                          const t = e.target.value as EmailBlock['type'];
                          if (t) setBlocks([...editor.blocks, defaultBlock(t)]);
                          e.target.value = '';
                        }}
                      >
                        <option value="">+ Add block…</option>
                        {BLOCK_TYPES.map((b) => <option key={b.type} value={b.type}>{b.label}</option>)}
                      </select>
                    )}
                  </div>

                  <div className={styles.blocks}>
                    {editor.blocks.map((block, i) => (
                      <BlockCard
                        key={i}
                        block={block}
                        index={i}
                        total={editor.blocks.length}
                        disabled={readOnly}
                        onChange={(nb) => setBlocks(editor.blocks.map((b, idx) => (idx === i ? nb : b)))}
                        onMove={(dir) => {
                          const j = i + dir;
                          if (j < 0 || j >= editor.blocks.length) return;
                          const next = [...editor.blocks];
                          [next[i], next[j]] = [next[j] as EmailBlock, next[i] as EmailBlock];
                          setBlocks(next);
                        }}
                        onRemove={() => setBlocks(editor.blocks.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </div>
                </div>

                {/* Live web preview — server-rendered by the SAME renderer the
                    public page uses; injecting it is safe-by-construction (the
                    renderer escapes every string; blocks carry no raw HTML). */}
                <div className={styles.previewSide}>
                  <div className={styles.previewLabel}>Web preview (server-rendered)</div>
                  <div className={styles.previewFrame}>
                    {editor.title && <h1 className={styles.previewTitle}>{editor.title}</h1>}
                    {previewErr
                      ? <p className={styles.err}>{previewErr}</p>
                      : <div className={styles.previewBody} dangerouslySetInnerHTML={{ __html: previewHtml }} />}
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div className={styles.actions}>
                {!readOnly && (
                  <button type="button" className={styles.saveBtn} disabled={!canSave || busy}
                    onClick={() => editor && save.mutate(editor)}>
                    {save.isPending ? 'Saving…' : editor.id ? 'Save draft' : 'Create draft'}
                  </button>
                )}
                {editor.status === 'draft' && editor.id && (
                  <button type="button" className={styles.approveBtn} disabled={dirty || busy}
                    title={dirty ? 'Save your changes before approving' : undefined}
                    onClick={() => {
                      if (window.confirm('Approve this post? Approval is a human sign-off; it unlocks the site preview and publishing.')) approve.mutate(editor.id!);
                    }}>
                    <ShieldCheck size={15} /> {approve.isPending ? 'Approving…' : 'Approve'}
                  </button>
                )}
                {editor.status === 'approved' && editor.id && (
                  <button type="button" className={styles.publishBtn} disabled={busy}
                    onClick={() => {
                      if (window.confirm('Publish this post? It goes live on the public blog API immediately.')) publish.mutate(editor.id!);
                    }}>
                    <Globe size={15} /> {publish.isPending ? 'Publishing…' : 'Publish'}
                  </button>
                )}
                {editor.status === 'published' && editor.id && (
                  <button type="button" className={styles.unpublishBtn} disabled={busy}
                    onClick={() => {
                      if (window.confirm('Unpublish this post? It leaves the public blog and returns to an editable draft.')) unpublish.mutate(editor.id!);
                    }}>
                    <Undo2 size={15} /> {unpublish.isPending ? 'Unpublishing…' : 'Unpublish'}
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// ── Block editor card (same vocabulary + controls as the newsletter editor) ──

function TextInput(props: { value: string; label: string; disabled: boolean; onChange: (v: string) => void; textarea?: boolean; placeholder?: string }) {
  return (
    <label className={styles.blockField}>
      <span>{props.label}</span>
      {props.textarea
        ? <textarea disabled={props.disabled} value={props.value} placeholder={props.placeholder} onChange={(e) => props.onChange(e.target.value)} />
        : <input disabled={props.disabled} value={props.value} placeholder={props.placeholder} onChange={(e) => props.onChange(e.target.value)} />}
    </label>
  );
}

function BlockCard(props: {
  block: EmailBlock;
  index: number;
  total: number;
  disabled: boolean;
  onChange: (b: EmailBlock) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { block, index, total, disabled, onChange, onMove, onRemove } = props;
  const patch = (p: Record<string, unknown>) => onChange({ ...block, ...p } as EmailBlock);
  const typeLabel = BLOCK_TYPES.find((t) => t.type === block.type)?.label ?? block.type;

  return (
    <div className={styles.blockCard}>
      <div className={styles.blockCardHead}>
        <span className={styles.blockType}>{typeLabel}</span>
        {!disabled && (
          <span className={styles.blockCtrls}>
            <button type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp size={14} /></button>
            <button type="button" title="Move down" disabled={index === total - 1} onClick={() => onMove(1)}><ArrowDown size={14} /></button>
            <button type="button" title="Remove" className={styles.blockRemove} onClick={onRemove}><Trash2 size={14} /></button>
          </span>
        )}
      </div>

      {block.type === 'heading' && (
        <div className={styles.blockBody}>
          <TextInput label="Text" disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
          <label className={styles.blockField}>
            <span>Level</span>
            <select disabled={disabled} value={block.level ?? 2} onChange={(e) => patch({ level: Number(e.target.value) })}>
              <option value={1}>1 — section title</option>
              <option value={2}>2 — default</option>
              <option value={3}>3 — sub-heading</option>
            </select>
          </label>
        </div>
      )}

      {block.type === 'paragraph' && (
        <div className={styles.blockBody}>
          <TextInput label="Text" textarea disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
        </div>
      )}

      {block.type === 'image' && (
        <div className={styles.blockBody}>
          <TextInput label="Image URL (https)" disabled={disabled} value={block.url} placeholder="https://…" onChange={(v) => patch({ url: v })} />
          <TextInput label="Alt text" disabled={disabled} value={block.alt} onChange={(v) => patch({ alt: v })} />
          <TextInput label="Link URL (optional)" disabled={disabled} value={block.href ?? ''} placeholder="https://…" onChange={(v) => patch({ href: v || undefined })} />
        </div>
      )}

      {block.type === 'image_row' && (
        <div className={styles.blockBody}>
          {block.images.map((img, ii) => (
            <div key={ii} className={styles.imageRowItem}>
              <TextInput label={`Image ${ii + 1} URL`} disabled={disabled} value={img.url} placeholder="https://…"
                onChange={(v) => patch({ images: block.images.map((x, xi) => (xi === ii ? { ...x, url: v } : x)) })} />
              <TextInput label="Alt" disabled={disabled} value={img.alt}
                onChange={(v) => patch({ images: block.images.map((x, xi) => (xi === ii ? { ...x, alt: v } : x)) })} />
              {!disabled && block.images.length > 2 && (
                <button type="button" className={styles.smallRemove} onClick={() => patch({ images: block.images.filter((_, xi) => xi !== ii) })}>Remove image</button>
              )}
            </div>
          ))}
          {!disabled && block.images.length < 3 && (
            <button type="button" className={styles.smallAdd} onClick={() => patch({ images: [...block.images, { url: '', alt: '' }] })}>+ Add image</button>
          )}
        </div>
      )}

      {block.type === 'button' && (
        <div className={styles.blockBody}>
          <TextInput label="Label" disabled={disabled} value={block.label} onChange={(v) => patch({ label: v })} />
          <TextInput label="Link URL (https)" disabled={disabled} value={block.href} placeholder="https://…" onChange={(v) => patch({ href: v })} />
        </div>
      )}

      {block.type === 'video_poster' && (
        <div className={styles.blockBody}>
          <TextInput label="Poster image URL (https)" disabled={disabled} value={block.posterUrl} placeholder="https://…" onChange={(v) => patch({ posterUrl: v })} />
          <TextInput label="Video/page link (https)" disabled={disabled} value={block.href} placeholder="https://…" onChange={(v) => patch({ href: v })} />
          <TextInput label="Alt text" disabled={disabled} value={block.alt} onChange={(v) => patch({ alt: v })} />
        </div>
      )}

      {block.type === 'list' && (
        <div className={styles.blockBody}>
          <label className={styles.blockField}>
            <span>Style</span>
            <select disabled={disabled} value={block.style} onChange={(e) => patch({ style: e.target.value })}>
              <option value="bullet">Bulleted</option>
              <option value="numbered">Numbered</option>
            </select>
          </label>
          {block.items.map((it, ii) => (
            <div key={ii} className={styles.imageRowItem}>
              <TextInput label={`Item ${ii + 1}`} textarea disabled={disabled} value={it}
                onChange={(v) => patch({ items: block.items.map((x, xi) => (xi === ii ? v : x)) })} />
              {!disabled && (
                <span className={styles.blockCtrls}>
                  <button type="button" title="Move up" disabled={ii === 0}
                    onClick={() => { const n = [...block.items]; [n[ii - 1], n[ii]] = [n[ii] as string, n[ii - 1] as string]; patch({ items: n }); }}><ArrowUp size={14} /></button>
                  <button type="button" title="Move down" disabled={ii === block.items.length - 1}
                    onClick={() => { const n = [...block.items]; [n[ii], n[ii + 1]] = [n[ii + 1] as string, n[ii] as string]; patch({ items: n }); }}><ArrowDown size={14} /></button>
                  {block.items.length > 1 && (
                    <button type="button" className={styles.smallRemove} onClick={() => patch({ items: block.items.filter((_, xi) => xi !== ii) })}>Remove</button>
                  )}
                </span>
              )}
            </div>
          ))}
          {!disabled && block.items.length < 50 && (
            <button type="button" className={styles.smallAdd} onClick={() => patch({ items: [...block.items, 'New item'] })}>+ Add item</button>
          )}
        </div>
      )}

      {block.type === 'quote' && (
        <div className={styles.blockBody}>
          <TextInput label="Quote text" textarea disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
          <TextInput label="Attribution (optional)" disabled={disabled} value={block.attribution ?? ''} onChange={(v) => patch({ attribution: v || undefined })} />
        </div>
      )}

      {block.type === 'offer' && (
        <div className={styles.blockBody}>
          <TextInput label="Heading" disabled={disabled} value={block.heading} onChange={(v) => patch({ heading: v })} />
          <TextInput label="Body" textarea disabled={disabled} value={block.body} onChange={(v) => patch({ body: v })} />
          <TextInput label="Code (optional)" disabled={disabled} value={block.code ?? ''} onChange={(v) => patch({ code: v || undefined })} />
          <TextInput label="Button label (optional)" disabled={disabled} value={block.buttonLabel ?? ''} onChange={(v) => patch({ buttonLabel: v || undefined })} />
          <TextInput label="Button URL (optional, https)" disabled={disabled} value={block.buttonHref ?? ''} placeholder="https://…" onChange={(v) => patch({ buttonHref: v || undefined })} />
        </div>
      )}

      {block.type === 'divider' && <div className={styles.blockBody}><div className={styles.dividerPreview} /></div>}
    </div>
  );
}
